import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  const heroImages = [
    '/museo 1.jpg',
    '/museo 2.jpg',
    '/museo 3.jpg',
    '/museo 4.jpg'
  ];

  const [currentImage, setCurrentImage] = useState(0);
  const [noticiaActiva, setNoticiaActiva] = useState(null);
  const [noticias, setNoticias] = useState([]);
  const [videos, setVideos] = useState([]);
  const [playVideo, setPlayVideo] = useState(false); // Estado para reproducir iframe en el Home

  useEffect(() => {
    const cargarData = async () => {
      try {
        const [resNoticias, resVideos] = await Promise.all([
          fetch('https://logia-production-725b.up.railway.app/api/noticias'),
          fetch('https://logia-production-725b.up.railway.app/api/videos')
        ]);
        
        const dataNoticias = await resNoticias.json();
        const dataVideos = await resVideos.json();

        if (dataNoticias.success) setNoticias(dataNoticias.data);
        if (dataVideos.success) setVideos(dataVideos.data);
      } catch (error) {
        console.error("Error cargando data pública:", error);
      }
    };
    cargarData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [noticiaActiva]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const obtenerResumen = (texto) => {
    if (!texto) return '';
    return texto.length > 150 ? texto.substring(0, 150) + '...' : texto;
  };

  // Helper para extraer el ID de YouTube
  const getYouTubeId = (url) => {
    if (!url) return '';
    if (url.includes('v=')) return url.split('v=')[1].substring(0, 11);
    if (url.includes('youtu.be/')) return url.split('youtu.be/')[1].substring(0, 11);
    return '';
  };

  const videoPrincipal = videos.length > 0 ? videos[0] : null;

  if (noticiaActiva) {
    return (
      <div className="min-h-screen bg-[#050505] text-white font-sans pt-24 pb-24 selection:bg-[#C5A059] selection:text-[#050505]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-12 animate-fade-in">
          <button 
            onClick={() => setNoticiaActiva(null)}
            className="flex items-center gap-2 text-[#C5A059] text-xs tracking-widest uppercase hover:text-white transition-colors mb-12 group"
          >
            <svg className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            Volver al Inicio
          </button>

          <div className="mb-10 text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-[#050505] bg-[#C5A059] px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold">
                Actualidad
              </span>
              <span className="text-gray-500 text-[11px] tracking-widest uppercase font-mono">
                {formatearFecha(noticiaActiva.fecha_creacion)}
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-8">
              {noticiaActiva.titulo}
            </h1>
          </div>

          <div className="w-full aspect-[16/9] md:aspect-[21/9] bg-gray-900 border border-white/10 shadow-2xl p-2 bg-[#0a0a0a] mb-12">
            <img 
              src={noticiaActiva.foto_url || "https://images.unsplash.com/photo-1507676184212-d0350eb05dda?w=1200"} 
              alt={noticiaActiva.titulo} 
              className="w-full h-full object-cover opacity-90"
            />
          </div>

          <div className="prose prose-invert prose-lg max-w-none text-gray-300 font-light leading-relaxed text-justify mx-auto">
            {(noticiaActiva.articulo).split('\n\n').map((parrafo, idx) => {
              if (idx === 0) {
                return (
                  <p key={idx} className="text-gray-300 font-light leading-relaxed text-lg md:text-xl text-justify mb-8">
                    <span className="float-left text-7xl md:text-8xl text-[#C5A059] font-serif pr-4 leading-[0.8] mt-2 drop-shadow-md">
                      {parrafo.charAt(0)}
                    </span>
                    {parrafo.substring(1)}
                  </p>
                );
              }
              return (
                <p key={idx} className="text-gray-400 font-light leading-relaxed text-base md:text-lg text-justify mb-8">
                  {parrafo}
                </p>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#C5A059] selection:text-[#050505] font-sans">
      
      {/* 1. Hero Section */}
      <section className="relative w-full h-screen flex flex-col items-center justify-center overflow-hidden bg-[#050505]">
        {heroImages.map((img, index) => (
          <div 
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out ${
              index === currentImage ? 'opacity-40 md:opacity-50 scale-105' : 'opacity-0 scale-100'
            }`}
            style={{ backgroundImage: `url('${img}')` }}
          ></div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/90 via-transparent to-[#050505]"></div>
        <div className="relative z-10 text-center px-4 w-full mt-10">
          <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-normal text-white mb-2 md:mb-6 tracking-tight animate-fade-in [animation-delay:0.5s] drop-shadow-2xl">Gran Logia</h1>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl italic font-light text-gray-300 animate-fade-in [animation-delay:1s]">del Perú</h2>
        </div>
        <div className="absolute bottom-8 w-full flex flex-col items-center justify-center animate-fade-in [animation-delay:2s] z-20">
          <span className="text-[9px] tracking-[0.3em] uppercase text-gray-400 mb-4">Descubrir</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-[#C5A059] to-transparent overflow-hidden">
            <div className="w-full h-1/2 bg-white animate-slide-down"></div>
          </div>
        </div>
      </section>

     {/* 2. Sección Bienvenida Oficial */}
      <section className="relative z-20 bg-[#050505] py-24 md:py-32 px-6 lg:px-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
          <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
            <h3 className="font-serif text-3xl md:text-5xl text-white mb-8 leading-tight">Bienvenida</h3>
            <p className="text-gray-300 font-light leading-relaxed text-base md:text-lg mb-6">
              La Gran Logia de los Antiguos, Libres y Aceptados Masones de la República del Perú, también conocida como La Gran Logia del Perú, es la única institución que representa a la masonería regular. Es reconocida por las más de 150 Grandes Logias del Mundo, además de fundadora e integrante de la Confederación Masónica interamericana (C.M.I.) y participante activo de la Conferencia de Grandes Logias Regulares.
            </p>
            <p className="text-gray-400 font-light leading-relaxed text-sm md:text-base mb-10">
              Al presente año 2026, está conformada por 234 Logias jurisdiccionadas en casi la totalidad de los Departamentos del Perú.
            </p>
            <div className="inline-block border border-[#C5A059]/30 p-4 rounded-sm">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-1">Contacto Oficial</span>
              <a href="mailto:webmaster@glperu.org.pe" className="text-[#C5A059] hover:text-white transition-colors text-sm tracking-wide">webmaster@glperu.org.pe</a>
            </div>
          </div>
          <div className="flex-1 w-full order-1 lg:order-2 flex items-center justify-center">
            <div className="aspect-square md:aspect-[4/3] w-full border border-[#C5A059]/20 p-3 relative flex items-center justify-center bg-[#0a0a0a]">
              <img src="/bienvenida.jpg" alt="Logo Bienvenida" className="max-w-[140px] max-h-[140px] w-auto h-auto object-contain grayscale hover:grayscale-0 transition-all duration-1000" />
            </div>
          </div>
        </div>
      </section>

      {/* 3. Sección de Noticias */}
      <section className="relative z-20 bg-[#0a0a0a] py-24 px-6 lg:px-12 border-b border-white/5">
        <div className="max-w-[100rem] mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-[#C5A059] text-[10px] tracking-[0.3em] uppercase mb-2 block">Actualidad</span>
              <h3 className="font-serif text-3xl md:text-4xl text-white font-normal">Últimas Noticias</h3>
            </div>
            <Link to="/noticias" className="hidden md:block text-xs tracking-[0.2em] uppercase text-gray-500 hover:text-[#C5A059] transition-colors">Ver todas +</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {noticias.slice(0, 3).map((noticia) => (
              <article 
                key={noticia.id} 
                className="group cursor-pointer flex flex-col"
                onClick={() => setNoticiaActiva(noticia)}
              >
                <div className="w-full aspect-[4/3] bg-gray-900 overflow-hidden mb-6 relative">
                  <img 
                    src={noticia.foto_url || "https://images.unsplash.com/photo-1507676184212-d0350eb05dda?w=600"} 
                    alt={noticia.titulo} 
                    className="w-full h-full object-cover grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-105" 
                  />
                  <div className="absolute top-4 left-4 lg:hidden group-hover:block transition-all duration-300">
                    <span className="text-[#050505] text-[9px] tracking-[0.2em] uppercase bg-[#C5A059] px-3 py-1 font-semibold shadow-md">
                      Institucional
                    </span>
                  </div>
                </div>
                <span className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase mb-3 block">{formatearFecha(noticia.fecha_creacion)}</span>
                <h4 className="font-serif text-xl text-white mb-3 group-hover:text-[#C5A059] transition-colors">{noticia.titulo}</h4>
                <p className="text-gray-400 text-sm font-light leading-relaxed line-clamp-3">{obtenerResumen(noticia.articulo)}</p>
              </article>
            ))}
          </div>
          
          <div className="mt-12 text-center md:hidden">
            <Link to="/noticias" className="inline-block text-xs tracking-[0.2em] uppercase text-[#C5A059] border border-[#C5A059]/50 px-6 py-3 hover:bg-[#C5A059] hover:text-[#050505] transition-colors">
              Ver Todas las Noticias
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Sección de Videos (Modificada a iFrame interactivo) */}
      <section className="relative z-20 bg-[#050505] py-24 px-6 lg:px-12 border-b border-white/5">
        <div className="max-w-[100rem] mx-auto">
          
          <div className="flex justify-between items-end mb-12">
            <div className="text-center md:text-left w-full md:w-auto">
              <span className="text-[#C5A059] text-[10px] tracking-[0.3em] uppercase mb-2 block">Multimedia</span>
              <h3 className="font-serif text-3xl md:text-4xl text-white font-normal">Registro Audiovisual</h3>
            </div>
            <Link to="/videos" className="hidden md:block text-xs tracking-[0.2em] uppercase text-gray-500 hover:text-[#C5A059] transition-colors">Ver galería completa +</Link>
          </div>

          {videoPrincipal ? (
            <div className="relative w-full max-w-5xl mx-auto aspect-video bg-[#0a0a0a] border border-[#C5A059]/20 overflow-hidden shadow-2xl">
              {!playVideo ? (
                <div 
                  className="w-full h-full cursor-pointer group" 
                  onClick={() => setPlayVideo(true)}
                >
                  <img src={videoPrincipal.cover_url} alt={videoPrincipal.titulo} className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent"></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-[#C5A059] bg-[#050505]/60 backdrop-blur-md flex items-center justify-center group-hover:bg-[#C5A059] transition-colors duration-500 mb-6 transform group-hover:scale-110">
                      <svg className="w-6 h-6 md:w-8 md:h-8 text-[#C5A059] group-hover:text-[#050505] ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    <h4 className="text-white font-serif text-2xl md:text-3xl lg:text-4xl tracking-wide drop-shadow-lg text-center px-4 max-w-3xl">
                      {videoPrincipal.titulo}
                    </h4>
                  </div>
                </div>
              ) : (
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${getYouTubeId(videoPrincipal.youtube_url)}?autoplay=1`}
                  title={videoPrincipal.titulo}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              )}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/5 bg-[#0a0a0a]">
              <p className="text-gray-500 text-sm tracking-widest uppercase">No hay videos disponibles</p>
            </div>
          )}

          <div className="mt-12 text-center md:hidden">
            <Link to="/videos" className="inline-block text-xs tracking-[0.2em] uppercase text-[#C5A059] border border-[#C5A059]/50 px-6 py-3 hover:bg-[#C5A059] hover:text-[#050505] transition-colors">
              Ver Galería Completa
            </Link>
          </div>
        </div>
      </section>

      {/* 5. Accesos Rápidos con Banner Nuevo */}
      <section className="relative z-20 max-w-[100rem] mx-auto px-4 md:px-8 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <Link to="/despacho/decretos" className="md:col-span-8 group relative overflow-hidden h-[400px] md:h-[500px] bg-[#0a0a0a] border border-white/5 flex flex-col justify-end p-8 md:p-12 hover:border-[#C5A059]/50 transition-colors duration-500">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1589829085413-56de8ae18c73?q=80')] bg-cover bg-center opacity-10 group-hover:opacity-30 transition-opacity duration-700 scale-105 group-hover:scale-100"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent z-10"></div>
            <div className="relative z-20 transform md:translate-y-4 md:group-hover:translate-y-0 transition-transform duration-500">
              <span className="text-[#C5A059] text-[10px] tracking-[0.3em] uppercase mb-4 block">Decretos</span>
              <h3 className="font-serif text-3xl md:text-5xl text-white font-normal mb-4">Comunicados Oficiales</h3>
              <p className="text-gray-400 text-sm max-w-lg font-light md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500 delay-100">
                Acceda al repositorio de decretos, resoluciones y circulares emitidas por la Gran Secretaría.
              </p>
            </div>
          </Link>

          <Link to="/organismos/instituto-historico" className="md:col-span-4 group relative overflow-hidden h-[400px] md:h-[500px] bg-[#0a0a0a] border border-white/5 flex flex-col justify-end p-8">
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent z-10"></div>
            <div className="relative z-20">
              <span className="text-[#C5A059] text-[10px] tracking-[0.3em] uppercase mb-4 block">Legado</span>
              <h3 className="font-serif text-2xl md:text-3xl text-white font-normal mb-4">Instituto Histórico</h3>
              <div className="w-8 h-[1px] bg-[#C5A059] group-hover:w-full transition-all duration-700 ease-out"></div>
            </div>
          </Link>

          <Link to="/despacho" className="md:col-span-4 group relative overflow-hidden h-[300px] md:h-[400px] bg-[#0a0a0a] border border-white/5 flex flex-col justify-between p-8 hover:border-[#C5A059]/50 transition-colors duration-500">
            <div className="flex justify-end">
              <svg className="w-6 h-6 text-[#C5A059]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
            <div>
              <h3 className="font-serif text-2xl md:text-3xl text-white font-normal mb-2">Trámites y Despacho</h3>
              <p className="text-gray-500 text-xs tracking-wide">Documentación oficial.</p>
            </div>
          </Link>

          <Link to="/intranet" className="md:col-span-8 group relative overflow-hidden h-[300px] md:h-[400px] bg-gradient-to-br from-[#C5A059] to-[#9A7B3E] flex flex-col justify-center items-center p-8">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-30 mix-blend-overlay"></div>
            <div className="relative z-20 text-center">
              <h3 className="font-serif text-3xl md:text-5xl text-[#050505] font-semibold mb-4 transform md:group-hover:-translate-y-2 transition-transform duration-500">
                Acceso Intranet
              </h3>
              <span className="inline-block border border-[#050505] text-[#050505] px-6 py-2 text-xs tracking-[0.2em] uppercase hover:bg-[#050505] hover:text-[#C5A059] transition-colors">
                Ingresar al Portal
              </span>
            </div>
          </Link>

          {/* NUEVO BANNER: Quiero Ser Masón */}
          <Link to="/quiero-ser-mason" className="md:col-span-12 group relative overflow-hidden h-[250px] md:h-[300px] bg-[#0a0a0a] border border-[#C5A059]/30 flex flex-col justify-center items-center p-8 hover:border-[#C5A059] transition-all duration-500 shadow-xl mt-2">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1541888086925-921eb234c900?q=80')] bg-cover bg-center opacity-10 group-hover:opacity-20 transition-opacity duration-700"></div>
            <div className="relative z-20 text-center flex flex-col items-center">
              <span className="text-[#C5A059] text-[10px] tracking-[0.4em] uppercase mb-3 block font-semibold">Admisión</span>
              <h3 className="font-serif text-4xl md:text-6xl text-white font-normal mb-5 group-hover:text-[#C5A059] transition-colors drop-shadow-md">
                Quiero ser Masón
              </h3>
              <span className="text-gray-400 text-xs tracking-widest uppercase border-b border-[#C5A059]/50 pb-1 group-hover:text-white transition-colors">
                Conozca los requisitos y postule a la Orden
              </span>
            </div>
          </Link>

        </div>
      </section>
    </div>
  );
};

export default Home;